/**
 * @file useZoneSectionOperations.js
 * @description Zone section operations for AHUs (Air Handling Units)
 *
 * Extracted from UnifiedBrickSchemaManager.vue
 * Handles:
 * - Zone section creation and configuration
 * - Zone duplication
 * - Zone template creation
 * - Zone renaming
 */

import { ref } from 'vue';
import { useQuasar } from 'quasar';

export function useZoneSectionOperations() {
  const $q = useQuasar();

  // ============================================================================
  // STATE
  // ============================================================================

  const showZoneSectionDialog = ref(false);
  const selectedAHU = ref(null);
  const selectedZoneTemplate = ref(null);
  const savingZoneSection = ref(false);

  const newZoneSection = ref({
    name: '',
    zone_type: 'mixing',
    serves_space: null,
    brick_class: 'AHU_Zone_Section',
  });

  // ============================================================================
  // DIALOG METHODS
  // ============================================================================

  /**
   * Show add zone section dialog
   * @param {Object} node - AHU node
   * @param {Function} isAHU - Function to check if node is an AHU
   * @param {Function} isTemplatedEquipment - Function to check if equipment is templated
   */
  const showAddZoneSectionDialog = (node, isAHU, isTemplatedEquipment) => {
    if (!isAHU(node)) {
      $q.notify({
        type: 'negative',
        message: 'Zone sections can only be added to Air Handling Units',
      });
      return;
    }

    if (isTemplatedEquipment(node)) {
      $q.notify({
        type: 'warning',
        message: 'This AHU was created from a template and cannot be modified',
        caption: 'Zone sections for templated equipment are managed through the template',
        icon: 'warning',
        timeout: 5000,
      });
      return;
    }

    selectedAHU.value = node.data;
    newZoneSection.value = {
      name: '',
      zone_type: 'mixing',
      serves_space: null,
      brick_class: 'AHU_Zone_Section',
    };
    showZoneSectionDialog.value = true;
  };

  /**
   * Reset zone section dialog
   */
  const resetZoneSectionDialog = () => {
    newZoneSection.value = {
      name: '',
      zone_type: 'mixing',
      serves_space: null,
      brick_class: 'AHU_Zone_Section',
    };
    selectedAHU.value = null;
    selectedZoneTemplate.value = null;
  };

  /**
   * Apply zone template to form
   * @param {Object|null} template - Zone template to apply
   */
  const applyZoneTemplate = (template) => {
    if (!template) {
      newZoneSection.value = {
        name: '',
        zone_type: 'mixing',
        serves_space: null,
        brick_class: 'AHU_Zone_Section',
      };
      return;
    }

    newZoneSection.value = {
      ...newZoneSection.value,
      zone_type: template.zone_type || 'mixing',
      brick_class: 'AHU_Zone_Section',
      template_id: template.id,
    };

    if (!newZoneSection.value.name) {
      newZoneSection.value.name = `${template.name} - ${selectedAHU.value?.name || 'Zone'}`;
    }
  };

  // ============================================================================
  // ZONE OPERATIONS
  // ============================================================================

  /**
   * Rename zone section
   * @param {Object} node - Zone section node
   * @param {Function} authenticatedFetch - Authenticated fetch function
   * @param {number} siteId - Site ID
   * @param {Function} refreshDataWithState - Callback to refresh data
   */
  const renameZoneSection = async (node, authenticatedFetch, siteId, refreshDataWithState) => {
    $q.dialog({
      title: 'Rename Zone Section',
      message: 'Enter a new name for this zone section:',
      prompt: {
        model: node.data?.name || node.label || '',
        type: 'text',
        isValid: (val) => val && val.trim().length > 0,
      },
      cancel: true,
      persistent: true,
    }).onOk(async (newName) => {
      try {
        const response = await authenticatedFetch(`/api/v2/sites/${siteId}/equipment/${node.data.id}`, {
          method: 'PUT',
          body: JSON.stringify({
            name: newName.trim(),
            brick_class: node.data.brick_class || 'AHU_Zone_Section',
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to rename zone section');
        }

        $q.notify({
          type: 'positive',
          message: 'Zone section renamed successfully',
          icon: 'check',
        });

        await refreshDataWithState();
      } catch (error) {
        $q.notify({
          type: 'negative',
          message: 'Failed to rename zone section: ' + error.message,
          icon: 'error',
        });
      }
    });
  };

  /**
   * Duplicate zone
   * @param {Object} node - Zone node
   * @param {Object} api - API service
   * @param {Function} preserveTreeState - Callback to preserve tree state
   * @param {Function} loadHierarchyData - Callback to load hierarchy data
   * @param {Function} restoreTreeState - Callback to restore tree state
   */
  const duplicateZone = async (node, api, preserveTreeState, loadHierarchyData, restoreTreeState) => {
    $q.dialog({
      title: 'Duplicate Zone',
      message: 'Enter a name for the duplicated zone:',
      prompt: {
        model: `${node.data.name} (Copy)`,
        type: 'text',
        isValid: (val) => val && val.length > 0,
      },
      cancel: true,
      persistent: true,
    }).onOk(async (newName) => {
      try {
        const endpoint = `/v2/equipment/${node.data.id}/duplicate`;
        const duplicatedItem = await api.post(endpoint, {
          equipment_id: node.data.id,
          name: newName,
        });

        if (duplicatedItem) {
          $q.notify({
            type: 'positive',
            message: `Zone duplicated as "${newName}" successfully`,
          });

          const state = preserveTreeState();
          await loadHierarchyData();

          const nodesToExpand = [];
          if (node.data?.parent_equipment_id) {
            nodesToExpand.push(`equipment-${node.data.parent_equipment_id}`);
          }
          if (duplicatedItem.id) {
            nodesToExpand.push(`equipment-${duplicatedItem.id}`);
          }

          await restoreTreeState(state, nodesToExpand);
        }
      } catch (error) {
        $q.notify({
          type: 'negative',
          message: 'Failed to duplicate zone: ' + (error.message || 'Unknown error'),
        });
      }
    });
  };

  /**
   * Create zone template
   * @param {Object} node - Zone node
   * @param {Function} authenticatedFetch - Authenticated fetch function
   * @param {number} siteId - Site ID
   * @param {Function} loadEquipmentTemplates - Callback to load equipment templates
   */
  const createZoneTemplate = async (node, authenticatedFetch, siteId, loadEquipmentTemplates) => {
    $q.dialog({
      title: 'Create Zone Template',
      message: 'Enter a name for this zone template:',
      prompt: {
        model: '',
        type: 'text',
        isValid: (val) => val && val.length > 0,
      },
      cancel: true,
      persistent: true,
    }).onOk(async (templateName) => {
      try {
        const zoneId = node.data?.id || node.id;
        if (!zoneId) {
          throw new Error('Zone ID not found');
        }

        const response = await authenticatedFetch(`/api/v2/sites/${siteId}/equipment/${zoneId}/save-as-template`, {
          method: 'POST',
          body: JSON.stringify({
            name: templateName,
            description: `Template based on ${node.data?.name || node.label}`,
            include_devices: true,
            include_points: true,
            scope: 'site',
          }),
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || 'Failed to save template');
        }

        const result = await response.json();

        if (result.success) {
          $q.notify({
            type: 'positive',
            message: 'Zone template created successfully with all devices',
          });
          await loadEquipmentTemplates();
        }
      } catch (error) {
        $q.notify({
          type: 'negative',
          message: 'Failed to create template: ' + (error.message || 'Unknown error'),
        });
      }
    });
  };

  /**
   * Load existing feeds relationship for equipment
   * @param {Object} editingEquipment - Equipment being edited
   * @param {Function} authenticatedFetch - Authenticated fetch function
   * @param {number} siteId - Site ID
   */
  const loadEquipmentFeedsRelationship = async (editingEquipment, authenticatedFetch, siteId) => {
    try {
      const response = await authenticatedFetch(`/api/v2/sites/${siteId}/equipment/${editingEquipment.id}/relationships`);
      if (!response.ok) {return;}

      const data = await response.json();
      const feedsRelationships = data.data?.filter(
        (rel) => rel.relationship_type === 'feeds' && rel.target_type === 'Space',
      ) || [];

      if (feedsRelationships.length > 0) {
        editingEquipment.serves_space = feedsRelationships[0].target_id;
      }
    } catch (error) {
      console.error('Failed to load equipment feeds relationship:', error);
    }
  };

  // ============================================================================
  // RETURN
  // ============================================================================

  return {
    // State
    showZoneSectionDialog,
    selectedAHU,
    selectedZoneTemplate,
    savingZoneSection,
    newZoneSection,

    // Dialog methods
    showAddZoneSectionDialog,
    resetZoneSectionDialog,
    applyZoneTemplate,

    // Operations
    renameZoneSection,
    duplicateZone,
    createZoneTemplate,
    loadEquipmentFeedsRelationship,
  };
}
